import { useCallback, useEffect, useRef, useState } from 'react';
import { micConstraints } from '../lib/mic-device';
import { ApiError } from '../api/client';
import { isNativeApp } from '../lib/native-app';
import { useNativeAudioRecorder } from './useNativeAudioRecorder';
import {
  appendChunk,
  assembleBlob,
  beginRecording,
  deleteRecording,
  findUnfinished,
  gcFinishedRecordings,
  migrateUnfinishedRecordingKey,
  newRecordingId,
  type RecordingMeta,
} from '../lib/audio-recording-store';

/** Intervalo (ms) de cada `ondataavailable` — chunk persistido incrementalmente (M17.1). */
const TIMESLICE_MS = 2500;

/**
 * FASE 125 (125.C.1) — calibracao do medidor de volume em ESCALA PERCEPTUAL (dB).
 *
 * A curva antiga era RMS LINEAR (`rms * 3`): a fala normal produz um RMS baixo (~0.03-0.12) que,
 * multiplicado por 3, mal chegava a ~10-35% da barra — sub-representando a voz. Medidores de volume
 * profissionais usam ESCALA LOGARITMICA (dB) exatamente porque a percepcao humana de intensidade e
 * logaritmica. Mapeamos o RMS para dBFS (`20*log10(rms)`) e depois linearmente entre um PISO
 * (silencio de fundo) e um TETO (fala alta). Constantes NOMEADAS (nao magicas inline) para calibracao
 * futura (125.C.3 auto-calibracao fica DEFERIDA) sem reescrever a formula.
 *
 * -50 dBFS ~ silencio/ruido de sala; -8 dBFS ~ fala alta perto do microfone. Fala normal (~-30 a -20
 * dBFS) cai no meio-alto da barra, visivelmente maior que a curva antiga.
 */
export const AUDIO_METER_FLOOR_DB = -50;
export const AUDIO_METER_CEILING_DB = -8;

/**
 * FASE 125 (125.C.1) — converte um RMS (0..1) do buffer time-domain para um nivel de barra (0..1) em
 * escala perceptual (dB). PURO/testavel. Robusto: rms<=0 -> 0 (silencio, sem `log10(0)=-Infinity`);
 * satura em 1; nunca NaN/negativo. Alimenta o `audioLevel` do medidor (AudioMeter).
 */
export function rmsToMeterLevel(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) return 0;
  const db = 20 * Math.log10(Math.min(1, rms));
  const level = (db - AUDIO_METER_FLOOR_DB) / (AUDIO_METER_CEILING_DB - AUDIO_METER_FLOOR_DB);
  if (!Number.isFinite(level)) return 0;
  return Math.max(0, Math.min(1, level));
}

/**
 * FASE 122 (122.B.2): mostra segundos decorridos durante uma transcricao longa — o texto
 * estatico "transcrevendo…" nao distingue "ainda processando" de "travou", especialmente
 * relevante agora que o timeout do proxy escala com o tamanho do audio (122.B.1, ate 4min).
 * Retorna a funcao de parada (limpa o intervalo); nao mexe no `notice` apos parar.
 */
function startElapsedNotice(label: string, setNotice: (v: string | null) => void): () => void {
  const startedAt = Date.now();
  setNotice(label);
  const timer = setInterval(() => {
    const elapsedSec = Math.round((Date.now() - startedAt) / 1000);
    setNotice(`${label} (${elapsedSec}s)`);
  }, 1000);
  return () => clearInterval(timer);
}

/** Estado de uma gravacao inacabada recuperavel (M17.1.2 + FASE 123.C). */
export interface RecoverableRecording {
  recordingId: string;
  startedAt: number;
  /**
   * FASE 123.C: origem do estado recuperavel — 'reload' = gravacao inacabada detectada no MOUNT (reload no
   * meio, M17.1.2); 'failed' = a transcricao ACABOU de falhar (audio preservado para retry sem regravar). A UI
   * adapta a copy/CTA por este campo. Ambos reusam o mesmo fluxo `recover()` (re-assembla do IndexedDB).
   */
  reason: 'reload' | 'failed' | 'device';
}

/** ECO.18 — opções do gravador. `onBackgroundTranscript`: texto de uma gravação parada FORA do app (notificação). */
export interface AudioRecorderOptions {
  onBackgroundTranscript?: (text: string) => void;
}

export interface AudioRecorderApi {
  recording: boolean;
  transcribing: boolean;
  notice: string | null;
  audioLevel: number;
  recoverable: RecoverableRecording | null;
  /** ECO.18 — true no app Android: a gravação continua com a tela bloqueada ou o app minimizado. */
  background: boolean;
  start: () => Promise<void>;
  stop: () => Promise<string | null>;
  /** ECO4.8-S — para sem transcrever e descarta o áudio. */
  cancel: () => Promise<void>;
  recover: () => Promise<string | null>;
  discardRecoverable: () => Promise<void>;
  clearNotice: () => void;
}

/**
 * Gravador de voz de todos os consumidores (terminais, notas, chat, Assistente). No app Android (ECO.18) a captura
 * é nativa, em serviço de primeiro plano; no navegador, MediaRecorder com pedaços no IndexedDB (M17.1). A escolha é
 * fixa durante a vida da página, então a ordem dos hooks não muda entre renderizações.
 */
export function useAudioRecorder(
  transcribe: (blob: Blob, filename: string) => Promise<string>,
  recordingKey = 'chat',
  legacyRecordingKey?: string,
  options?: AudioRecorderOptions,
): AudioRecorderApi {
  // A plataforma não muda durante a vida da página: a ordem dos hooks é estável.
  if (isNativeApp()) return useNativeAudioRecorder(transcribe, recordingKey, options);
  return useWebAudioRecorder(transcribe, recordingKey, legacyRecordingKey);
}

/**
 * Hook de gravacao de audio reutilizavel (M5.9): encapsula o MediaRecorder + transcricao.
 * Usado pelo chat do orquestrador (M4.4) e pelos terminais de projeto (M5.9). Degrada
 * graciosamente: sem suporte a MediaRecorder ou microfone, expoe um aviso e nao quebra.
 *
 * M7.2: usa o microfone preferido (localStorage) + constraints de voz (echo/noise/AGC).
 * Cleanup (M8-WEB#31): para os tracks do microfone ao desmontar — nunca deixa o mic ligado.
 *
 * M17.1 (RESILIENCIA anti-perda): grava com `start(timeslice)` e PERSISTE cada chunk em IndexedDB
 * sob um recordingId. Um reload da pagina nao perde mais a gravacao: no proximo mount, uma gravacao
 * inacabada e detectada e oferecida para RECUPERAR (montar + transcrever) via `recoverable`/`recover`.
 * Tambem instala um guard `beforeunload` enquanto grava (M17.1.3).
 *
 * @param transcribe funcao que recebe o blob de audio e o filename e devolve o texto.
 * @param recordingKey chave do consumidor (sessionId do terminal ou 'chat') — vincula a gravacao
 *   persistida ao contexto, para recuperar a certa no mount. Default 'chat'.
 */
function useWebAudioRecorder(
  transcribe: (blob: Blob, filename: string) => Promise<string>,
  recordingKey = 'chat',
  legacyRecordingKey?: string,
): {
  recording: boolean;
  transcribing: boolean;
  notice: string | null;
  /**
   * FASE 123.D: nivel de volume do microfone (0..1) enquanto grava — confirma VISUALMENTE que o audio esta
   * sendo capturado de verdade. 0 quando nao grava ou o navegador nao suporta AudioContext (degrada gracioso).
   */
  audioLevel: number;
  /** Gravacao inacabada detectada no mount (reload no meio) — null se nao houver (M17.1.2). */
  recoverable: RecoverableRecording | null;
  background: boolean;
  /** Inicia a gravacao (pede permissao de microfone). */
  start: () => Promise<void>;
  /** Para e dispara a transcricao; resolve com o texto (ou null em falha/cancelamento). */
  stop: () => Promise<string | null>;
  cancel: () => Promise<void>;
  /** Recupera a gravacao inacabada: monta os chunks salvos + transcreve. Resolve o texto ou null. */
  recover: () => Promise<string | null>;
  /** Descarta a gravacao inacabada sem transcrever (limpa o IndexedDB). */
  discardRecoverable: () => Promise<void>;
  clearNotice: () => void;
} {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recoverable, setRecoverable] = useState<RecoverableRecording | null>(null);
  // FASE 123.D: medidor de volume (AnalyserNode sobre o MediaStream). Opcional — sem AudioContext o hook
  // segue igual (audioLevel fica 0).
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const resolveRef = useRef<((text: string | null) => void) | null>(null);
  const recordingIdRef = useRef<string | null>(null);
  // FASE 123.C: instante de inicio da gravacao atual (p/ o estado recuperavel pos-falha exibir "ha Ns").
  const startedAtRef = useRef<number>(0);

  // FASE 123.D: encerra o medidor de volume (cancela o rAF, desconecta o analyser, fecha o AudioContext).
  // Idempotente e best-effort — nunca lanca (o medidor e cosmetico; a gravacao/transcricao nao dependem dele).
  const stopMeter = (): void => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    try {
      analyserRef.current?.disconnect();
    } catch {
      /* noop */
    }
    analyserRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx) void ctx.close().catch(() => {});
    setAudioLevel(0);
  };

  // FASE 123.D: inicia o medidor de volume sobre o MediaStream ja capturado. Sem AudioContext (browser antigo
  // ou jsdom nos testes) e no-op — a gravacao continua e audioLevel fica 0 (degradacao graciosa).
  const startMeter = (stream: MediaStream): void => {
    try {
      const w = window as unknown as {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      };
      const Ctx = w.AudioContext ?? w.webkitAudioContext;
      if (!Ctx || typeof requestAnimationFrame !== 'function') return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = (): void => {
        const a = analyserRef.current;
        if (!a) return;
        a.getByteTimeDomainData(buf);
        // RMS do sinal em torno de 128 (silencio) -> 0..1, escalado para visibilidade da fala.
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i]! - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        // FASE 125 (125.C.1): escala PERCEPTUAL (dB) em vez de linear (`rms * 3`) — a fala normal
        // agora responde de forma visivel na barra (ver rmsToMeterLevel).
        setAudioLevel(rmsToMeterLevel(rms));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      /* medidor e opcional — nunca impede a gravacao */
    }
  };

  // M17.1.2: ao montar, procura uma gravacao INACABADA para esta chave (reload no meio da gravacao)
  // e a oferece para recuperar. Roda 1x por chave.
  useEffect(() => {
    let alive = true;
    void (async () => {
      if (legacyRecordingKey) {
        await migrateUnfinishedRecordingKey(legacyRecordingKey, recordingKey);
      }
      const meta = await findUnfinished(recordingKey);
      if (alive && meta) {
        setRecoverable({
          recordingId: meta.recordingId,
          startedAt: meta.startedAt,
          reason: 'reload',
        });
      }
    })();
    // FASE 133 (133.C.2): GC oportunista das gravacoes CONCLUIDAS antigas (>7d) — o "boot" do lado web.
    // Fire-and-forget/best-effort; NAO toca as recuperaveis (finished=false, reload/failed — 123.C intocado).
    void gcFinishedRecordings();
    return () => {
      alive = false;
    };
  }, [legacyRecordingKey, recordingKey]);

  // M17.1.3: guard beforeunload enquanto grava — avisa antes de recarregar/fechar com audio ativo.
  useEffect(() => {
    if (!recording) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Browsers modernos ignoram a string custom, mas exigem returnValue setado p/ mostrar o prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [recording]);

  // Cleanup (M8-WEB#31): ao desmontar (ex: sair de /chat gravando), para a gravacao e LIBERA o
  // microfone. Antes, o ChatComposer reimplementava o audio e deixava os tracks vivos -> o mic
  // ficava ligado (luz acesa) ao sair. Com o hook unico, o cleanup vale para todos os consumidores.
  // M17.1: NAO apaga os chunks persistidos — uma gravacao interrompida por unmount fica recuperavel.
  useEffect(() => {
    return () => {
      try {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch {
        /* noop */
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      // FASE 123.D: encerra o medidor (libera o AudioContext) ao desmontar.
      stopMeter();
    };
  }, []);

  const start = async (): Promise<void> => {
    setNotice(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setNotice('gravacao de audio nao suportada neste navegador');
      return;
    }
    try {
      // M7.2: device preferido + constraints de voz (echoCancellation/noiseSuppression/AGC).
      const stream = await navigator.mediaDevices.getUserMedia(micConstraints());
      streamRef.current = stream;
      chunksRef.current = [];
      // FASE 123.D: liga o medidor de volume (confirma visualmente a captura enquanto grava).
      startMeter(stream);
      const rec = new MediaRecorder(stream);

      // M17.1: nova gravacao -> registra o meta (finished=false) no IndexedDB para resiliencia.
      const recordingId = newRecordingId();
      recordingIdRef.current = recordingId;
      const meta: RecordingMeta = {
        recordingId,
        key: recordingKey,
        mimeType: rec.mimeType || 'audio/webm',
        startedAt: Date.now(),
        finished: false,
      };
      startedAtRef.current = meta.startedAt;
      void beginRecording(meta);
      // Uma nova gravacao substitui qualquer recuperavel anterior na UI (estamos gravando de novo).
      setRecoverable(null);

      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) {
          chunksRef.current.push(ev.data);
          // M17.1: persiste o chunk incrementalmente — sobrevive a reload.
          void appendChunk(recordingId, ev.data);
        }
      };
      rec.onstop = () => void handleRecorded();
      recorderRef.current = rec;
      // M17.1: timeslice => ondataavailable a cada TIMESLICE_MS (chunks duraveis, memoria baixa).
      rec.start(TIMESLICE_MS);
      setRecording(true);
    } catch {
      setNotice('nao foi possivel acessar o microfone (permissao negada?)');
    }
  };

  const cancelledRef = useRef(false);
  /** ECO4.8-S — cancelar: para sem transcrever e descarta o áudio. */
  const cancel = async (): Promise<void> => {
    if (!recorderRef.current || !recording) return;
    cancelledRef.current = true;
    await stop();
  };

  const stop = (): Promise<string | null> => {
    if (!recorderRef.current || !recording) {
      return Promise.resolve(null);
    }
    return new Promise<string | null>((resolve) => {
      resolveRef.current = resolve;
      recorderRef.current?.stop();
      setRecording(false);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      // FASE 123.D: para o medidor de volume ao encerrar a gravacao.
      stopMeter();
    });
  };

  const handleRecorded = async (): Promise<void> => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    const recordingId = recordingIdRef.current;
    // ECO4.8-S — cancelado: joga fora o áudio (memória e IndexedDB) sem transcrever.
    if (cancelledRef.current) {
      cancelledRef.current = false;
      chunksRef.current = [];
      if (recordingId) void deleteRecording(recordingId);
      recordingIdRef.current = null;
      setNotice('gravação descartada');
      resolve?.(null);
      return;
    }
    const blob = new Blob(chunksRef.current, {
      type: recorderRef.current?.mimeType || 'audio/webm',
    });
    chunksRef.current = [];
    if (blob.size === 0) {
      // Gravacao vazia: limpa o rastro persistido para nao oferecer "recuperar" de um vazio.
      if (recordingId) void deleteRecording(recordingId);
      recordingIdRef.current = null;
      resolve?.(null);
      return;
    }
    setTranscribing(true);
    // FASE 122 (122.B.2): "transcrevendo… (Ns)" — segundos decorridos, nao mais texto estatico.
    const stopTicker = startElapsedNotice('transcrevendo…', setNotice);
    try {
      const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
      const text = await transcribe(blob, `recording.${ext}`);
      setNotice(null);
      // M17.1: transcricao OK -> limpa os chunks persistidos (nao ha mais o que recuperar).
      if (recordingId) void deleteRecording(recordingId);
      recordingIdRef.current = null;
      resolve?.(text);
    } catch (err) {
      // FASE 123.C.1 (GARANTIA "jamais perder o audio"): NAO markFinished na falha. Antes, markFinished
      // removia o registro de findUnfinished E escondia a opcao de recuperar — o audio (bytes no IndexedDB)
      // ficava orfao, sem caminho na UI para retry ao vivo (so pos-reload). Agora o registro fica RECUPERAVEL
      // (reason 'failed') e o usuario pode reenviar o MESMO blob via `recover()` (123.C.2), sem regravar. So
      // sai do estado recuperavel quando a transcricao SUCEDE (deleteRecording) ou o usuario DESCARTA.
      if (recordingId) {
        setRecoverable({
          recordingId,
          startedAt: startedAtRef.current || Date.now(),
          reason: 'failed',
        });
      }
      setNotice(friendlyTranscribeError(err));
      resolve?.(null);
    } finally {
      stopTicker();
      setTranscribing(false);
    }
  };

  // M17.1.2: recupera a gravacao inacabada (reload no meio) — monta os chunks + transcreve.
  const recover = useCallback(async (): Promise<string | null> => {
    const rec = recoverable;
    if (!rec) return null;
    setTranscribing(true);
    setNotice('recuperando audio…');
    try {
      const blob = await assembleBlob(rec.recordingId);
      if (!blob || blob.size === 0) {
        await deleteRecording(rec.recordingId);
        setRecoverable(null);
        setNotice('nao havia audio recuperavel');
        return null;
      }
      // FASE 122 (122.B.2): mesmo indicador de tempo decorrido do fluxo normal.
      const stopTicker = startElapsedNotice('transcrevendo…', setNotice);
      try {
        const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
        const text = await transcribe(blob, `recording.${ext}`);
        await deleteRecording(rec.recordingId);
        setRecoverable(null);
        setNotice(null);
        return text;
      } finally {
        stopTicker();
      }
    } catch (err) {
      // FASE 123.C: um retry que falha de novo NAO perde o audio — mantem o registro recuperavel e marca
      // 'failed' para o CTA "tentar novamente" permanecer disponivel (o blob segue no IndexedDB).
      setNotice(friendlyTranscribeError(err));
      setRecoverable((prev) => (prev ? { ...prev, reason: 'failed' } : prev));
      return null;
    } finally {
      setTranscribing(false);
    }
  }, [recoverable, transcribe]);

  const discardRecoverable = useCallback(async (): Promise<void> => {
    const rec = recoverable;
    if (!rec) return;
    await deleteRecording(rec.recordingId);
    setRecoverable(null);
    setNotice(null);
  }, [recoverable]);

  return {
    recording,
    transcribing,
    notice,
    audioLevel,
    recoverable,
    background: false,
    start,
    stop,
    cancel,
    recover,
    discardRecoverable,
    clearNotice: () => setNotice(null),
  };
}

/**
 * FASE 122 (122.A.1): classifica pelo campo TIPADO `ApiError.code` (o `reason` exato que o
 * servidor mandou), nunca por regex sobre o texto livre da mensagem — o texto do `detail` pode
 * mudar em qualquer refator futuro sem quebrar esta classificacao de novo, silenciosamente (era
 * exatamente o que acontecia: o caso de timeout caia no fallback generico e enganoso porque o
 * `detail` nao continha as substrings "503"/"upstream_unreachable" que a regex procurava).
 */
export function friendlyTranscribeError(err: unknown): string {
  if (err instanceof ApiError) {
    // ECO.18 — sessão expirada/sem prova de escrita: o áudio fica guardado; entrar de novo resolve.
    if (err.status === 401 || err.status === 403) return 'sua sessão expirou — recarregue ou entre de novo; o áudio ficou guardado';
    switch (err.code) {
      case 'not_configured':
        return 'transcricao indisponivel: servico nao configurado no servidor';
      // FASE 122 (122.A.2): timeout (audio longo demais para o proxy) e um caso DISTINTO de
      // "Mac mini offline" — orienta a acao certa (encurtar o audio) em vez de sugerir um
      // problema de infraestrutura que nao e o real.
      case 'timeout':
        return 'o servico de transcricao demorou demais (audio muito longo?) — tente um audio mais curto ou digite o texto';
      case 'upstream_unreachable':
        return 'servico de transcricao offline (Mac mini) — digite o texto';
      case 'upstream_error':
        return 'servico de transcricao respondeu com erro — digite o texto';
      default:
        break;
    }
  }
  return 'falha ao transcrever o audio — digite o texto';
}
